import { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { XIcon } from '@air/design-icons';
import { Button } from '@air/primitive-button';

import { SearchFieldClearButton } from '~/components/Search/SearchFieldClearButton';
import { useAppliedFiltersCount } from '~/hooks/filters/useAppliedFiltersCount';
import { useApplyFilters } from '~/hooks/filters/useApplyFilters';
import { useSearchParams } from '~/hooks/search/useSearchParams';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { clearAllFiltersAction } from '~/store/filters/actions';

export type PublicSearchActionsProps = {
  onClearSearchValue: () => void;
};

export const PublicSearchActions = memo(({ onClearSearchValue }: PublicSearchActionsProps) => {
  const { search } = useSearchParams();
  const { filtersCount } = useAppliedFiltersCount();
  const { clearSearchAndFilters } = useApplyFilters();
  const dispatch = useDispatch();
  const { ui2026 } = useTemporaryFeatureFlags();
  const label = filtersCount > 0 ? 'Clear all' : 'Clear';

  const handleClick = useCallback(() => {
    clearSearchAndFilters();
    dispatch(clearAllFiltersAction());
    onClearSearchValue();
  }, [onClearSearchValue, clearSearchAndFilters, dispatch]);

  if (!filtersCount && !search) {
    return null;
  }

  if (ui2026) {
    return <SearchFieldClearButton data-testid="CLEAR_FILTER_BUTTON" label={label} onClick={handleClick} />;
  }

  return (
    <Button
      appearance="outline"
      color="gray"
      data-testid="CLEAR_FILTER_BUTTON"
      onClick={handleClick}
      prefix={<XIcon size="xs" />}
      size="small"
    >
      {label}
    </Button>
  );
});

PublicSearchActions.displayName = 'PublicSearchActions';
